/* Vertragsdaten eines Spielers — reine Funktionen, kein DOM, keine Datenbank.
 *
 * Seit Stufe 3 (Teil 2) liegen die Vertragsdaten nicht mehr im Datenblock
 * players.data (transferInfo, clubContracts), sondern in eigenen Tabellen:
 *   player_contracts       eine Zeile je Spieler
 *   player_contract_files  eine Zeile je Vertragsdokument
 * Sie sind echt typisiert: Ablöse in Euro und Weiterverkaufsanteil in Prozent
 * als Zahlen, Vertragsende als Datum.
 *
 * In der App hängen sie am Spielerobjekt, ausschließlich im Speicher:
 *   player.contract               Formular  { club, type, contractEnd, fee, sellOn }
 *   player.contractFiles          [{ id, path, filename, label, signedDate, note, _new? }]
 *   player.removedContractFiles   entfernte Einträge, erst beim Speichern gelöscht
 * Diese Schlüssel gehören NICHT in den Datenblock — stripTransient
 * (domain/playerShape.js) entfernt sie vor dem Schreiben. Sie werden nur für
 * Rollen mit Recht (admin, board, data) geladen; für alle anderen fehlen sie.
 * Fehlt `contract`, wurde nicht (erfolgreich) geladen: dann darf nichts
 * gespeichert werden, sonst würde eine leere Fassung echte Daten überschreiben.
 */

export const CONTRACT_TYPES = ['permanent', 'loan'];

export function emptyContract() {
  return { club: '', type: '', contractEnd: '', fee: '', sellOn: '' };
}

/** Zahl oder null; '' / null / nicht endlich → null. */
function num(v) {
  if (v === '' || v == null) return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function contractFromRow(row) {
  if (!row) return emptyContract();
  return {
    club: row.club || '',
    type: row.contract_type || '',
    contractEnd: row.contract_end || '',
    fee: row.fee_eur == null ? '' : Number(row.fee_eur),
    sellOn: row.sell_on_pct == null ? '' : Number(row.sell_on_pct),
  };
}

/** Zeile für die Tabelle; leere Eingaben werden zu null. */
export function contractToRow(playerId, form) {
  const f = form || emptyContract();
  return {
    player_id: playerId,
    club: String(f.club || '').trim() || null,
    contract_type: f.type || null,
    contract_end: f.contractEnd || null,
    fee_eur: num(f.fee),
    sell_on_pct: num(f.sellOn),
  };
}

/**
 * Steht in den Vertragsfeldern, die der Reiter Transfer Info zeigt (Art, Ende, Ablöse, Sell-on),
 * etwas? Der Vereinsname der Vertragszeile zählt nicht: Er wird nirgends angezeigt. 0 ist ein Wert.
 */
export function hasVisibleContractData(form) {
  const r = contractToRow(null, form);
  return !!r.contract_type || !!r.contract_end || r.fee_eur != null || r.sell_on_pct != null;
}

export function isContractEmpty(form) {
  const r = contractToRow(null, form);
  return !r.club && !r.contract_type && !r.contract_end && r.fee_eur == null && r.sell_on_pct == null;
}

/** Fehlerschlüssel der Eingabe; leere Liste = in Ordnung. Spiegelt die Regeln der Tabelle. */
export function validateContract(form) {
  const f = form || emptyContract();
  const errors = [];
  if (f.type && !CONTRACT_TYPES.includes(f.type)) errors.push('type');
  if (f.fee !== '' && f.fee != null) {
    const n = num(f.fee);
    if (n == null || n < 0) errors.push('fee');
  }
  if (f.sellOn !== '' && f.sellOn != null) {
    const n = num(f.sellOn);
    if (n == null || n < 0 || n > 100) errors.push('sellOn');
  }
  return errors;
}

export function contractFileFromRow(row) {
  return {
    id: row.id,
    path: row.path,
    filename: row.filename || '',
    label: row.label || '',
    signedDate: row.signed_date || '',
    note: row.note || '',
  };
}

export function contractFileToRow(playerId, f) {
  return {
    id: f.id,
    player_id: playerId,
    path: f.path,
    filename: f.filename || null,
    label: f.label || null,
    signed_date: f.signedDate || null,
    note: f.note || null,
  };
}

/** Schlüssel, die nur im Speicher leben und nie in den Datenblock gehören. */
export const CONTRACT_MEMORY_KEYS = ['contract', 'contractFiles', 'removedContractFiles'];

/** Vertragsfelder an ein Spielerobjekt hängen (ohne vorhandene zu überschreiben). */
export function attachContract(player, row, fileRows = []) {
  if (player.contract === undefined) {
    player.contract = contractFromRow(row);
    player.contractFiles = fileRows.map(contractFileFromRow);
    player.removedContractFiles = [];
  }
  return player;
}

/** Vertragsfelder wieder abnehmen (Abmelden, Rollenwechsel). */
export function detachContract(player) {
  for (const k of CONTRACT_MEMORY_KEYS) delete player[k];
  return player;
}
